import React from "react";
import { useTranslation } from "react-i18next";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import { TextField, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import { Controller, Resolver, useForm } from "react-hook-form";
import {
  IEmailList,
  IEmailRecipient,
} from "../../../shared/interfaces/email-list/IEmailList";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import Grid from "@mui/material/Grid2";
import { AdminEmailListService } from "../service";
import { handleError } from "../../../shared/functions/handleError";
import { useParams } from "react-router-dom";
import { enqueueSnackbar } from "notistack";

interface IProps {
  open: boolean;
  setDate: React.Dispatch<React.SetStateAction<IEmailList | undefined>>;
  handleClose: () => void;
}
export const emailRecipientSchema = yup.object({
  email: yup.string().email("Invalid email").required("validation.required"),
  fullName: yup.string().required("validation.required"),
  companyName: yup.string().optional(),
  websiteUrl: yup.string().url("Invalid URL").optional(),
  region: yup.string().optional(),
  status: yup.string().required("validation.required"),
});

const EmailListAddRecipientModal = ({ open, handleClose, setDate }: IProps) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { handleSubmit, control, reset } = useForm<IEmailRecipient>({
    resolver: yupResolver(
      emailRecipientSchema,
    ) as unknown as Resolver<IEmailRecipient>,
    defaultValues: {
      status: "PENDING",
    } as unknown as IEmailRecipient,
  });

  const handleCancel = () => {
    handleClose();
    reset({
      status: "PENDING",
    });
  };

  const onSubmit = async (formState: IEmailRecipient) => {
    !!id &&
      (await AdminEmailListService.addRecipient(formState, id)
        .then((response) => {
          if (response) {
            setDate(
              (prev) =>
                ({
                  ...prev,
                  recipients: response?.concat(prev?.recipients),
                }) as IEmailList,
            );
            enqueueSnackbar(
              t("toasts.successfullyCreated", { title: formState?.fullName }),
              {
                variant: "success",
              },
            );
            handleCancel();
          }
        })
        .catch(handleError));
  };
  return (
    <Modal
      open={open}
      onClose={handleCancel}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box
        className={"MuiModalBody"}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          width: { xs: "80%", lg: "30%" },
        }}
      >
        <Typography id="modal-modal-title" variant="h5">
          {t("general.addRecipient")}
        </Typography>
        <Box sx={{ marginTop: "30px", marginBottom: "30px", width: "100%" }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Controller
                name="fullName"
                control={control}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <TextField
                    label={t("general.displayName")}
                    fullWidth
                    value={value ?? ""}
                    onChange={onChange}
                    error={!!error?.message}
                    helperText={t(error?.message || "")}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Controller
                name="email"
                control={control}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <TextField
                    label={t("general.email")}
                    fullWidth
                    value={value ?? ""}
                    onChange={onChange}
                    type={"email"}
                    error={!!error?.message}
                    helperText={t(error?.message || "")}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Controller
                name="companyName"
                control={control}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <TextField
                    label={t("emails.companyName")}
                    fullWidth
                    value={value ?? ""}
                    onChange={onChange}
                    error={!!error?.message}
                    helperText={t(error?.message || "")}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                name="websiteUrl"
                control={control}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <TextField
                    label={t("emails.websiteUrl")}
                    fullWidth
                    value={value ?? ""}
                    onChange={onChange}
                    error={!!error?.message}
                    helperText={t(error?.message || "")}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                name="region"
                control={control}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <TextField
                    label={t("emails.region")}
                    fullWidth
                    value={value ?? ""}
                    onChange={onChange}
                    error={!!error?.message}
                    helperText={t(error?.message || "")}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Box>
        <div>
          <Button
            variant={"contained"}
            color={"secondary"}
            size={"small"}
            onClick={handleCancel}
            sx={{ marginRight: "10px" }}
          >
            {t("button.cancel")}
          </Button>
          <Button
            variant={"contained"}
            size={"small"}
            onClick={handleSubmit(onSubmit)}
          >
            {t("button.create")}
          </Button>
        </div>
      </Box>
    </Modal>
  );
};
export default EmailListAddRecipientModal;
